import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrIcon from 'client/components/SrIcon';
import './SrStepsChecklist.less';

export const SR_STEPS_CHECKLIST_STATUS_FAILED = 'Failed';
export const SR_STEPS_CHECKLIST_STATUS_NOT_STARTED = 'NotStarted';
export const SR_STEPS_CHECKLIST_STATUS_IN_PROGRESS = 'InProgress';
export const SR_STEPS_CHECKLIST_STATUS_COMPLETED = 'Completed';

export const SR_STEPS_CHECKLIST_STATUS_TO_CLASSNAME_MAPPING = {
  [SR_STEPS_CHECKLIST_STATUS_FAILED]: 'statusFailed',
  [SR_STEPS_CHECKLIST_STATUS_NOT_STARTED]: 'statusNotStarted',
  [SR_STEPS_CHECKLIST_STATUS_IN_PROGRESS]: 'statusInProgress',
  [SR_STEPS_CHECKLIST_STATUS_COMPLETED]: 'statusCompleted',
};

const SrStepsChecklist = ({
  steps,
  checkMarkInsteadNumber,
  labelLineThroughOnCompleted,
}) => {
  return (
    <div>
      {steps.map((step, index) => {
        const {
          label,
          labelColor,
          status,
          onClick,
          onCtaClick,
          content,
          contentLeftPadding,
          sublabel,
          ctaLabel,
          stepChecklistItems,
          stepChecklistButton,
          alwaysShowChecklistItems,
        } = step;

        return (
          <div
            key={step.label}
            styleName={classnames(
              'step',
              SR_STEPS_CHECKLIST_STATUS_TO_CLASSNAME_MAPPING[status],
              onClick && 'clickable',
            )}
            onClick={() => onClick && onClick(index)}
          >
            <SrFlex justify='between'>
              <SrFlex>
                {status === SR_STEPS_CHECKLIST_STATUS_IN_PROGRESS
                  ? (
                    <img
                      styleName='stepIconSpinner'
                      alt='progress spinner'
                      src='/images/yellow-spinner.svg'
                    />
                  )
                  : (
                    checkMarkInsteadNumber ? (
                      <div styleName='stepIcon'>
                        <SrIcon className='checkmark-white' />
                      </div>
                    ) : (
                      <div styleName='stepIcon'>
                        {index + 1}
                      </div>
                    )
                  )
                }
                <div
                  styleName={
                    classnames(
                      'label',
                      labelLineThroughOnCompleted
                      && status === SR_STEPS_CHECKLIST_STATUS_COMPLETED
                        ? 'labelLineThrough'
                        : '',
                    )
                  }
                  style={{ color: labelColor }}
                >
                  {label}
                </div>
                {ctaLabel && (
                  <span onClick={onCtaClick} styleName={classnames('ctaLabel', onCtaClick && 'clickable')}>
                    {ctaLabel}
                  </span>
                )}
              </SrFlex>
              {sublabel && (
                <div styleName='sublabel'>
                  {sublabel}
                </div>
              )}
            </SrFlex>
            {content && (
              <div styleName={classnames('content', contentLeftPadding && 'withLeftPadding')}>
                {content}
              </div>
            )}
            {stepChecklistItems
              && (status === SR_STEPS_CHECKLIST_STATUS_IN_PROGRESS || alwaysShowChecklistItems)
              && (
                <SrBox marginTop='16px' marginBottom='16px'>
                  {stepChecklistItems.map((item) => (
                    <SrFlex key={item.label} marginTop='10px' marginLeft='26px' align='start'>
                      {item.status === SR_STEPS_CHECKLIST_STATUS_COMPLETED
                        ? (
                          <img
                            alt='checkmark'
                            src='/images/checkmark-small-rounded-green.svg'
                            width='16px'
                            style={{
                              marginRight: '16px',
                              marginLeft: '2px',
                              marginTop: '6px',
                            }}
                          />
                        )
                        : item.status === SR_STEPS_CHECKLIST_STATUS_FAILED
                          ? (
                            <img
                              alt='error'
                              src='/images/x-error-icon.svg'
                              width='13px'
                              style={{
                                marginRight: '17px',
                                marginLeft: '4px',
                                marginBottom: '-1px',
                                marginTop: '6px',
                              }}
                            />
                          )
                          : (
                            <SrBox
                              width='10px'
                              height='10px'
                              borderRadius='6px'
                              backgroundColor='#D2D5D7'
                              opacity='0.4'
                              display='inlineBlock'
                              marginRight='20px'
                              marginLeft='4px'
                              marginTop='6px'
                            />
                          )
                      }
                      <SrText
                        font='greycliff'
                        color='blueGreycliff'
                        size='16'
                        lineHeight='20px'
                        letterSpacing='0'
                        weight='500'
                      >
                        {item.label}
                      </SrText>
                    </SrFlex>
                  ))}
                </SrBox>
              )}
            {stepChecklistButton
              && alwaysShowChecklistItems
              && (
                <SrBox marginBottom='32px'>
                  <SrText
                    color='blue300'
                    weight='800'
                    font='greycliff'
                    letterSpacing='0'
                    onClick={stepChecklistButton.onClick}
                    cursor='pointer'
                    align='center'
                    display='block'
                  >
                    {stepChecklistButton.buttonText}
                  </SrText>
                </SrBox>
              )}
          </div>
        );
      })}
    </div>
  );
};

SrStepsChecklist.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    labelColor: PropTypes.string,
    status: PropTypes.oneOf([
      SR_STEPS_CHECKLIST_STATUS_NOT_STARTED,
      SR_STEPS_CHECKLIST_STATUS_IN_PROGRESS,
      SR_STEPS_CHECKLIST_STATUS_COMPLETED,
      SR_STEPS_CHECKLIST_STATUS_FAILED,
    ]),
    onClick: PropTypes.func,
    onCtaClick: PropTypes.func,
    content: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.node,
    ]),
    contentLeftPadding: PropTypes.bool,
    sublabel: PropTypes.string,
    ctaLabel: PropTypes.string,
    stepChecklistItems: PropTypes.arrayOf(PropTypes.object.isRequired),
    stepChecklistButton: PropTypes.object,
    alwaysShowChecklistItems: PropTypes.bool,
  }).isRequired).isRequired,
  checkMarkInsteadNumber: PropTypes.bool,
};

SrStepsChecklist.displayName = 'SrStepsChecklist';

export default SrStepsChecklist;
